import type { JSX } from 'react';
import { Box } from '@ksyos/design-system';
import { useState } from 'react';
import type { IObesiQQuestionnaireResponseWithTitle } from '@common/customControls/bariatrics';
import type {
    BariPostCaroQuestionnaireResponseReadData,
    BariPostCaroQuestionnaireResponseReadKey,
} from '@common/validation/customControls/bariPost/bariPostCaroQuestionnaireResponse/v1/bariPostCaroQuestionnaireResponseSchemaV1';
import type { ComponentsMap } from '@frontend/formEngine/readForm/readForm.types';
import { ObesiQState } from '@common/enums/obesiQState';
import { getProcessActivityReadSectionItem } from '@common/utils/processActivityReadUtil';
import { ProcessActivityReadContext } from '@frontend/components/pages/episodeCategory/carePath/process/processActivity/processActivityRead/processActivityReadContext';
import { ReadForm } from '@frontend/formEngine/readForm/ReadForm';
import { useDefinedContext } from '@frontend/utils/hooks/useDefinedContext';
import { NavigateToMonitoringTabLink } from '../../NavigateToMonitoringTabLink';
import { BariPostCaroQuestionnaireResponseFormGroup } from './BariPostCaroQuestionnaireResponseFormGroup';

export function BariPostCaroQuestionnaireResponseRead(): JSX.Element {
    const { readSections } = useDefinedContext(ProcessActivityReadContext);
    const [renderMonitoringTabLink, setRenderMonitoringTabLink] = useState<boolean>(false);

    return (
        <Box
            display="flex"
            flexDirection="column"
            gap={5}
        >
            <ReadForm components={getComponents()} />
            {renderMonitoringTabLink && <NavigateToMonitoringTabLink />}
        </Box>
    );

    function getComponents(): ComponentsMap {
        const map: ComponentsMap = new Map();
        addQuestionnaireResponseComponents(map);

        return map;
    }

    function addQuestionnaireResponseComponents(map: ComponentsMap): void {
        const readItemKeys: BariPostCaroQuestionnaireResponseReadKey[] = [
            'eetgedrag',
            'sociaal_welzijn',
            'psychisch_welbevinden',
            'lichamelijke_activiteiten',
            'lichaamsbeeld',
            'seksueel_welzijn',
        ];
        for (const readItemKey of readItemKeys) {
            const readSectionItem = getProcessActivityReadSectionItem<BariPostCaroQuestionnaireResponseReadData>()(
                readSections,
                readItemKey
            );

            if (!readSectionItem || !isIObesiQQuestionnaireResponseWithTitle(readSectionItem.value)) {
                continue;
            }
            if (!renderMonitoringTabLink && readSectionItem.value.obesiQState === ObesiQState.notify) {
                setRenderMonitoringTabLink(true);
            }
            map.set(
                readSectionItem.key,
                <BariPostCaroQuestionnaireResponseFormGroup
                    key={readSectionItem.key}
                    label={readSectionItem.label}
                    values={readSectionItem.value}
                />
            );
        }
    }

    function isIObesiQQuestionnaireResponseWithTitle(
        value: string[] | IObesiQQuestionnaireResponseWithTitle
    ): value is IObesiQQuestionnaireResponseWithTitle {
        return (
            typeof value === 'object' &&
            value !== null &&
            'obesiQQuestionnaireTitle' in value &&
            'obesiQState' in value &&
            'raschTransformedScore' in value
        );
    }
}
